/**
 * Steel cut geometry utilities
 *
 * Turns the parametric cuts stored on a member (`element.steelCuts`) into
 * renderable sub-segments along the member's start->end axis:
 * - Modifier / Miter: trims the member at the cut station.
 * - Cope: trims the member end AND reduces the section over the cope length
 *   (simulating the relieved flange).
 * - CutThrough: kept as a contour marker (no split; the renderer draws the
 *   hole outline instead of boolean subtraction).
 *
 * Stations are parametric t values (0 = start, 1 = end) so segments stay
 * exact for inclined members.
 */

import * as THREE from 'three';
import { Element, SteelCutData } from '../core/types';
import { resolveMemberFrame } from '../core/SteelFabricationManager';

export interface MemberSegment {
    /** t parameters along the member axis. */
    t0: number;
    t1: number;
    /** Section override for coped zones (fractions of the original). */
    widthScale: number;
    depthScale: number;
    /** Vertical shift of the segment center as a fraction of depth, used by
     * top/bottom coping so the untouched flange stays aligned. */
    depthShift: number; // -1..1 (fraction of half-depth)
}

const EPS = 1e-4;

/** World cut station of a cut on the member axis, or null when not applicable. */
function cutStationAlongAxis(cut: SteelCutData, start: THREE.Vector3, dir: THREE.Vector3, length: number): number | null {
    let point: THREE.Vector3 | null = null;
    if (cut.plane) {
        point = new THREE.Vector3(...cut.plane.origin);
    } else if (cut.at === 'Start' || cut.at === 'End') {
        point = cut.at === 'Start' ? start.clone() : start.clone().addScaledVector(dir, length);
    }
    if (!point) return null; // Body cuts (CutThrough) do not split the axis
    const t = point.clone().sub(start).dot(dir) / Math.max(length, EPS);
    return THREE.MathUtils.clamp(t, 0, 1);
}

/**
 * Compute the render segments of a member. Returns a single full segment when
 * the element has no end-type cuts (fast path, matches pre-steel behavior).
 */
export function computeMemberSegments(element: Element): {
    start: THREE.Vector3;
    end: THREE.Vector3;
    width: number;
    depth: number;
    segments: MemberSegment[];
} | null {
    const frame = resolveMemberFrame(element);
    if (!frame) return null;
    const { start, end, width, depth } = frame;
    const dir = end.clone().sub(start);
    const length = dir.length();
    if (length < EPS) return null;
    dir.normalize();

    const cuts = element.steelCuts ?? [];
    const full: MemberSegment = { t0: 0, t1: 1, widthScale: 1, depthScale: 1, depthShift: 0 };
    if (cuts.length === 0) return { start, end, width, depth, segments: [full] };

    // 1) Collect trim stations + per-zone section overrides from cuts.
    let tMin = 0;
    let tMax = 1;
    interface Zone { t0: number; t1: number; widthScale: number; depthScale: number; depthShift: number }
    const zones: Zone[] = [];

    for (const cut of cuts) {
        const station = cutStationAlongAxis(cut, start, dir, length);
        if (station == null) continue;

        if (cut.kind === 'Cope') {
            // Cope removes material from one flange over `length` from the cut
            // end; the member is also trimmed back by the same length.
            const copeLenM = Math.max(cut.length ?? 0.1, 0.02);
            const copeT = Math.min(copeLenM / length, 0.5);
            const depthScale = THREE.MathUtils.clamp(1 - Math.max(cut.depth ?? 0.1, 0.02) / depth, 0.2, 0.95);
            if (cut.at === 'Start') {
                tMin = Math.max(tMin, station);
                zones.push({ t0: tMin, t1: Math.min(tMin + copeT, 1), widthScale: 1, depthScale, depthShift: cut.flange === 'Bottom' ? -1 : 1 });
            } else {
                tMax = Math.min(tMax, station);
                zones.push({ t0: Math.max(tMax - copeT, 0), t1: tMax, widthScale: 1, depthScale, depthShift: cut.flange === 'Bottom' ? -1 : 1 });
            }
        } else {
            // Modifier & Miter: pure trim at the station.
            const angleRad = THREE.MathUtils.degToRad(Math.abs(cut.angle ?? 0));
            if (cut.at === 'Start') {
                const inset = angleRad > EPS ? Math.tan(angleRad) * (depth / 2) / Math.max(length, EPS) : 0;
                tMin = Math.max(tMin, station + inset);
            } else if (cut.at === 'End') {
                const inset = angleRad > EPS ? Math.tan(angleRad) * (depth / 2) / Math.max(length, EPS) : 0;
                tMax = Math.min(tMax, station - inset);
            }
        }
    }

    if (tMax - tMin < EPS) return { start, end, width, depth, segments: [] };

    // 2) Merge zone boundaries into a partition of [tMin, tMax].
    const boundaries = new Set<number>([tMin, tMax]);
    zones.forEach(z => { boundaries.add(THREE.MathUtils.clamp(z.t0, tMin, tMax)); boundaries.add(THREE.MathUtils.clamp(z.t1, tMin, tMax)); });
    const sorted = Array.from(boundaries).sort((a, b) => a - b);

    const segments: MemberSegment[] = [];
    for (let i = 0; i < sorted.length - 1; i++) {
        const s0 = sorted[i];
        const s1 = sorted[i + 1];
        if (s1 - s0 < EPS) continue;
        const mid = (s0 + s1) / 2;
        const zone = zones.find(z => mid >= z.t0 - EPS && mid <= z.t1 + EPS);
        segments.push({
            t0: s0,
            t1: s1,
            widthScale: zone?.widthScale ?? 1,
            depthScale: zone?.depthScale ?? 1,
            depthShift: zone?.depthShift ?? 0,
        });
    }
    if (segments.length === 0) segments.push({ t0: tMin, t1: tMax, widthScale: 1, depthScale: 1, depthShift: 0 });

    return { start, end, width, depth, segments };
}

/** True when the member geometry is modified by cuts (skip fast path). */
export function memberHasRenderedCuts(element: Element): boolean {
    return (element.steelCuts ?? []).some(c => c.kind !== 'CutThrough');
}

// ==================== CutThrough Hole Cutters ====================

export interface HoleCutter {
    /** Parametric station along the member axis (0..1). */
    t: number;
    /** Cutter geometry baked in world space. */
    geometry: THREE.BufferGeometry;
}

/** Shoelace area of a 2D contour polygon (local contour units = meters). */
function contourArea(contour: [number, number][]): number {
    let area = 0;
    for (let i = 0; i < contour.length; i++) {
        const [x1, y1] = contour[i];
        const [x2, y2] = contour[(i + 1) % contour.length];
        area += x1 * y2 - x2 * y1;
    }
    return Math.abs(area / 2);
}

/**
 * Build world-baked cutter prisms for every CutThrough contour on a member.
 * The contour polygon is given in [along-axis, vertical-transverse] meters;
 * the cutter extrudes straight through the member's horizontal transverse
 * axis (the web), mirroring BIM's contour-cut behavior. The renderer
 * subtracts these with CSGHelper at the matching member segment.
 */
export function buildSteelHoleCutters(
    element: Element,
    frame: { start: THREE.Vector3; end: THREE.Vector3; width: number; depth: number }
): HoleCutter[] {
    const cuts = (element.steelCuts ?? []).filter(
        c => c.kind === 'CutThrough' && Array.isArray(c.contour) && c.contour.length >= 3
    );
    if (cuts.length === 0) return [];

    const { start, end, width } = frame;
    const dir = end.clone().sub(start);
    const length = dir.length();
    if (length < EPS) return [];
    dir.normalize();

    // Right-handed contour frame: local X = member axis (contour x), local Y
    // = up-ish transverse (contour y), local Z = horizontal transverse
    // (extrusion / through-web direction).
    const up = new THREE.Vector3(0, 0, 1);
    if (Math.abs(dir.dot(up)) > 0.99) up.set(1, 0, 0);
    const zAxis = new THREE.Vector3().crossVectors(dir, up).normalize();
    const yAxis = new THREE.Vector3().crossVectors(zAxis, dir).normalize();
    const basis = new THREE.Matrix4().makeBasis(dir, yAxis, zAxis);

    const cutters: HoleCutter[] = [];
    // Bake the same z Justification offset the renderer applies to the member
    // box so hole cutters and segment solids stay aligned in world space.
    const justification = (element.geometryData as any)?.justification as string | undefined;
    let justifyZ = 0;
    if (justification === 'Top') justifyZ = -frame.depth / 2;
    else if (justification === 'Bottom') justifyZ = frame.depth / 2;

    for (const cut of cuts) {
        const t = THREE.MathUtils.clamp(Number(cut.parameters?.['Station'] ?? 0.5), 0, 1);
        const center = start.clone().addScaledVector(dir, t * length);
        center.z += justifyZ;

        const shape = new THREE.Shape();
        cut.contour!.forEach(([x, y], i) => {
            if (i === 0) shape.moveTo(x, y);
            else shape.lineTo(x, y);
        });
        shape.closePath();

        // Overshoot the member width so the subtraction cleanly exits both faces.
        const penetration = width * 1.5 + 0.05;
        const geo = new THREE.ExtrudeGeometry(shape, { depth: penetration, bevelEnabled: false });
        geo.translate(0, 0, -penetration / 2);
        geo.applyMatrix4(basis);
        geo.translate(center.x, center.y, center.z);

        cutters.push({ t, geometry: geo });
    }
    return cutters;
}

// ==================== Cut Metrics ====================

export interface SteelMemberMetrics {
    /** Net cut length along the axis after end trims, meters. */
    cutLength: number;
    /** Removed hole volume from contour cuts, m3. */
    holeVolume: number;
    /** Approximate member weight, kg (box-section model). */
    weightKg: number;
}

/**
 * Derive fabrication metrics from the stored cuts. Uses the same segment
 * partition the renderer sees, so takeoff numbers and the visual agree.
 */
export function computeSteelMemberMetrics(element: Element): SteelMemberMetrics | null {
    const base = computeMemberSegments(element);
    if (!base) return null;
    const { width, depth, segments } = base;
    const length = base.start.distanceTo(base.end);

    const netLength = segments.reduce((acc, s) => acc + (s.t1 - s.t0) * length, 0);

    let holeVolume = 0;
    for (const cut of element.steelCuts ?? []) {
        if (cut.kind !== 'CutThrough' || !cut.contour || cut.contour.length < 3) continue;
        holeVolume += contourArea(cut.contour) * width;
    }

    const weightKg = Math.max(width * depth * netLength - holeVolume, 0) * 7850;
    return { cutLength: netLength, holeVolume, weightKg };
}
